import React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import {
    getLaunchDarklyConfig,
    launchDarklyFlagDefaults
} from '~/utils/launchdarkly-utils';
import constants from '~/utils/constants';

/**
 * Passthrough component used when LaunchDarkly is unavailable
 */
const LaunchDarklyPassthrough = ({
    children
}: {
    children: React.ReactNode;
}) => <>{children}</>;
LaunchDarklyPassthrough.displayName = 'LaunchDarklyPassthrough';

/**
 * Initialize the LaunchDarkly provider asynchronously
 *
 * This function returns a React component that wraps children with
 * the LaunchDarkly context. It must be called before rendering.
 *
 * The `timeout` option bounds how long `asyncWithLDProvider` waits for
 * initialization. On timeout, `asyncWithLDProvider` still resolves with the
 * real provider (it does not surface the timeout to this caller), after which
 * flag reads fall back to `launchDarklyFlagDefaults` via `useLaunchDarklyFlag`.
 * The `catch` below therefore only guards against unexpected initialization
 * errors.
 */
export const initLaunchDarklyProvider = async () => {
    const config = getLaunchDarklyConfig(window.location.hostname);

    try {
        const LDProvider = await asyncWithLDProvider({
            clientSideID: config.clientSideId,
            options: {
                streaming: true
            },
            flags: launchDarklyFlagDefaults,
            reactOptions: {
                useCamelCaseFlagKeys: false
            },
            timeout: constants.timings.LAUNCHDARKLY_INIT_TIMEOUT
        });

        return LDProvider;
    } catch {
        // Unexpected initialization error; render with defaults
        return LaunchDarklyPassthrough;
    }
};
